import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useRef } from 'react';
import cn from 'classnames';
import { Controller, useFormContext } from 'react-hook-form';
import { richTextValidation } from 'validators';
import { Column, RichTextEditor, Tooltip, Typography, useDevice, useIsInBottomSheet } from 'seagull';
import { QuestionCircle } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useRichTextVariableIntegration } from '~/components/AgreementStudioPage/TemplateVariables/hooks/useRichTextVariableIntegration';
import { VariableInsertButton } from '~/components/AgreementStudioPage/TemplateVariables/VariableInsertButton/VariableInsertButton';
import { VariablePickerPopover } from '~/components/VariablePickerPopover/VariablePickerPopover';
import { useLocaleKeys } from '~/i18n';
import { connectControllerToRichTextEditor } from '~/utils/react-hook-form.utils';
import styles from './ServiceTerms.module.scss';
export const GetServiceTermsTID = (parentTestId)=>getTestIds(parentTestId);
const ServiceTerms = (param)=>{
    let { testId, hideLabel = false, onChange = ()=>{}, disabled = false } = param;
    const { t: termsTexts } = useLocaleKeys('serviceBuilder.terms');
    const { isMobileDevice } = useDevice();
    const isInBottomSheet = useIsInBottomSheet();
    const showMobileCard = isMobileDevice && !isInBottomSheet;
    const { control, watch } = useFormContext();
    const isBundle = Boolean(watch('bundle'));
    const label = termsTexts.serviceLabel();
    const tooltipText = termsTexts.tooltip({
        type: isBundle ? 'bundle' : 'service'
    });
    const { ServiceTermsTestIds } = useMemo(()=>GetServiceTermsTID(testId), [
        testId
    ]);
    const editorElRef = useRef(null);
    const { enabled: variablesEnabled, toolbarButtonProps, pickerPopoverProps } = useRichTextVariableIntegration(editorElRef, {
        disabled,
        showDefaultValuePopover: false
    });
    const variableButton = variablesEnabled ? /*#__PURE__*/ _jsx(VariableInsertButton, _object_spread_props(_object_spread({}, toolbarButtonProps), {
        testId: ServiceTermsTestIds.VariableInsertButton()
    })) : null;
    const toolbarActions = /*#__PURE__*/ _jsx("div", {
        className: styles.toolbarActionsRow,
        children: variableButton
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(Column, {
                gap: 8,
                alignItems: "start",
                className: cn(styles.root, {
                    [styles.mobile]: showMobileCard
                }),
                children: [
                    !hideLabel && !showMobileCard && /*#__PURE__*/ _jsxs("span", {
                        className: styles.labelRow,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                skin: "secondary",
                                testId: ServiceTermsTestIds.Label(),
                                children: label
                            }),
                            /*#__PURE__*/ _jsx(Tooltip, {
                                title: tooltipText,
                                placement: "top",
                                children: /*#__PURE__*/ _jsx("span", {
                                    className: styles.tooltipIcon,
                                    "data-testid": ServiceTermsTestIds.TooltipIcon(),
                                    children: /*#__PURE__*/ _jsx(QuestionCircle, {
                                        size: 14
                                    })
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Controller, {
                        control: control,
                        name: "terms",
                        rules: richTextValidation(),
                        render: (renderProps)=>{
                            const connected = connectControllerToRichTextEditor(renderProps);
                            const originalEditorRef = connected.editorRef;
                            return /*#__PURE__*/ _jsx(RichTextEditor, _object_spread_props(_object_spread({
                                bi: {
                                    name: 'service_terms'
                                },
                                editorHeight: 302,
                                placeholder: termsTexts.placeholder(),
                                testId: testId
                            }, connected), {
                                editorRef: (el)=>{
                                    editorElRef.current = el;
                                    if (typeof originalEditorRef === 'function') {
                                        originalEditorRef(el);
                                    }
                                },
                                onChange: (v)=>{
                                    onChange(v);
                                    connected.onChange(v);
                                },
                                toolbarActions: toolbarActions,
                                disabled: disabled,
                                mobile: {
                                    renderAsCard: true,
                                    summaryLabel: label
                                }
                            }));
                        }
                    })
                ]
            }),
            variablesEnabled && /*#__PURE__*/ _jsx(VariablePickerPopover, _object_spread({}, pickerPopoverProps))
        ]
    });
};
export default ServiceTerms;
